import { Controller } from "@hotwired/stimulus"

export default class ConfirmationController extends Controller {
  static values = {
    url: String,
    turbo: Boolean,
    method: { type: String, default: "delete" },
    title: String,
    text: String,
    icon: String,
    confirmText: String,
    confirmColor: String,
    denyText: String,
    denyColor: String,
    cancelColor: String,
    redirectUrl: String,
    manualRedirect: { type: Boolean, default: false },
    html: { type: Boolean, default: false }
  }

  connect() {
    this.element.addEventListener("click", this.handleClick)
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleClick)
  }

  handleClick = (event) => {
    event.preventDefault()
    this.capturePostAnchor()
    this.confirm(event)
  }

  capturePostAnchor() {
    this.scrollContainer = this.element.closest('.post-feed-scroll')
    if (!this.scrollContainer) return

    const rootTop = this.scrollContainer.getBoundingClientRect().top
    const clickedPost = this.element.closest('.feed-post-frame')
    const visiblePost = Array.from(
      this.scrollContainer.querySelectorAll('.feed-post-frame')
    ).find(post => post.getBoundingClientRect().bottom > rootTop)

    this.postAnchor = visiblePost === clickedPost
      ? clickedPost.nextElementSibling || clickedPost.previousElementSibling
      : visiblePost
    this.postAnchorOffset = this.postAnchor?.getBoundingClientRect().top - rootTop
  }

  restorePostAnchor() {
    if (!this.postAnchor?.isConnected || !this.scrollContainer?.isConnected) return

    const rootTop = this.scrollContainer.getBoundingClientRect().top
    const currentOffset = this.postAnchor.getBoundingClientRect().top - rootTop
    this.scrollContainer.scrollTop += currentOffset - this.postAnchorOffset
  }

  hideAllTooltip() {
    for (const tooltip of document.querySelectorAll('.tooltip')) {
      tooltip.remove();
    }
  }

  confirm(event) {
    const form = event.target.closest("form")
    const mainUrl = this.urlValue || event.currentTarget.getAttribute("href")

    if (form && typeof $(form).valid === "function" && !$(form).valid()) return

    const hasDenyButton = Boolean(this.denyTextValue)

    Swal.fire({
      customClass: {
        popup: 'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
        htmlContainer: 'text-black-paint text-white'
      },
      animation: false,
      heightAuto: false,
      title: this.titleValue || "Are you sure?",
      text: this.textValue || "You won't be able to revert this!",
      icon: this.iconValue || "warning",
      showCancelButton: true,
      showDenyButton: hasDenyButton,
      allowOutsideClick: false,
      allowEscapeKey: false,
      confirmButtonColor: this.confirmColorValue || "#3085d6",
      denyButtonColor: this.denyColorValue || "#d33",
      cancelButtonColor: this.cancelColorValue || "#6c757d",
      confirmButtonText: this.confirmTextValue || "Yes",
      denyButtonText: this.denyTextValue || "Exit & Delete",
      didOpen: () => {
        this.hideAllTooltip();
      }
    }).then(result => {
      if (result.isConfirmed) {
        const dispatchEvent = event?.target?.dataset?.dispatchEvent;
        if (dispatchEvent) this.dispatchEvent(dispatchEvent);

        form ? form.requestSubmit() : this.sendRequest(mainUrl);
        return;
      }

      if (result.isDenied) {
        const dispatchEvent = event?.target?.dataset?.dispatchEvent;
        if (dispatchEvent) this.dispatchEvent(dispatchEvent);

        // Append parameter delete_chat=true to the target URL
        const deleteUrl = new URL(mainUrl, globalThis.location.origin)
        deleteUrl.searchParams.set("delete_chat", "true")

        this.sendRequest(deleteUrl.toString());
        return;
      }

      const disabledClass = this.element?.dataset?.disabledElement;
      if (!disabledClass) return;

      const disabledElement = this.element.closest("." + disabledClass);
      if (disabledElement) {
        disabledElement.style.cursor = 'pointer';
        const disableController = this.application.getControllerForElementAndIdentifier(disabledElement, 'disable-click')
        disableController?.enable()
      }
    })
  }

  async sendRequest(url) {
    const turboRequest = await this.loadTurboRequest()
    await turboRequest(url, {
      method: this.methodValue.toUpperCase(),
      turboStream: this.turboValue,
      html: this.htmlValue,
      manualRedirect: this.manualRedirectValue,
      onSuccess: (result, response) => {
        if (this.manualRedirectValue && this.hasRedirectUrlValue) {
          globalThis.location.href = this.redirectUrlValue
          return
        }

        if (this.turboValue) {
          requestAnimationFrame(() => {
            this.restorePostAnchor()
            requestAnimationFrame(() => this.restorePostAnchor())
          })
        } else if (this.hasRedirectUrlValue) {
          globalThis.location.href = this.redirectUrlValue
        }
      }
    })
  }

  dispatchEvent(name, detail = {}) {
    const event = new CustomEvent(name, { detail })
    document.dispatchEvent(event)
  }

  async loadTurboRequest() {
    const turboRequest = (await import("../helpers/api")).turboRequest
    return turboRequest
  }
}
